/**
 * 수강 옵션(스케줄) 표시 라벨 포맷 — 단일 소스.
 *
 * 동일 포맷이 수강회원관리 상세(Detail/index.js)와 목록 CSV 생성부(List/index.js)에
 * 각각 인라인으로 중복되어 있던 것을 여기로 추출했다.
 * 옵션 변경 모달 / 옵션 변경 이력 목록도 이 함수를 사용한다.
 *
 * 오프라인 옵션 포맷 (eje_server 이력 스냅샷 라벨과 동일 규칙):
 *   {class_round}기 | {title || '옵션명 없음'} | {요일} | {class_start_time} | {start_date}~{end_date}
 */

/**
 * class_dates(7자리 문자열, 앞자리부터 월~일)를 요일 문자열로 변환한다.
 * 예) '1010100' → '월수금'
 *
 * @param {string} classDates
 * @param {object} intl react-intl intl 객체
 * @returns {string}
 */
export function formatWeekdays(classDates, intl) {
  if (!classDates) {
    return '';
  }
  return classDates
    .split('')
    .map((classDate, index) => (+classDate !== 0 ? intl.formatMessage({ id: 'ID_WEEKDAY_' + (index + 1) }) : ''))
    .join('');
}

/**
 * 오프라인/컨설팅 스케줄 라벨.
 *
 * @param {object} schedule ClassOfflineSchedule (또는 ClassConsultingSchedule)
 * @param {object} intl
 * @returns {string}
 */
export function formatOfflineScheduleLabel(schedule, intl) {
  if (!schedule) {
    return '';
  }
  return [
    schedule.class_round ? `${schedule.class_round}기` : '',
    schedule.title ? schedule.title : '옵션명 없음',
    formatWeekdays(schedule.class_dates, intl),
    schedule.class_start_time || '',
    schedule.start_date || schedule.end_date ? `${schedule.start_date}~${schedule.end_date}` : '',
  ]
    .filter((part) => !!part)
    .join(' | ');
}

/**
 * 온라인(기수제) 스케줄 라벨.
 *
 * @param {object} schedule ClassOnlineSchedule
 * @returns {string}
 */
export function formatOnlineScheduleLabel(schedule) {
  if (!schedule) {
    return '';
  }
  return [
    schedule.class_round ? `${schedule.class_round}기` : '',
    schedule.title ? schedule.title : '옵션명 없음',
    `${schedule.start_date}~${schedule.end_date}`,
    schedule.class_start_time ? `${schedule.class_start_time}~${schedule.class_end_time}` : '강의 시간 없음',
  ]
    .filter((part) => !!part)
    .join(' | ');
}

/**
 * User_Class 한 건의 "수강옵션" 표시 문자열.
 * 오프라인/컨설팅 → 온라인 순으로 판단하며, 어느 쪽도 없으면 빈 문자열.
 *
 * @param {object} userClass User_Class (FK_class_offline_schedule / FK_class_consulting_schedule / FK_class_online_schedule 포함)
 * @param {object} intl
 * @returns {string}
 */
export function formatUserClassScheduleLabel(userClass, intl) {
  if (!userClass) {
    return '';
  }
  const offlineConsultingSchedule = userClass.FK_class_offline_schedule || userClass.FK_class_consulting_schedule;
  if (offlineConsultingSchedule) {
    return formatOfflineScheduleLabel(offlineConsultingSchedule, intl);
  }
  if (userClass.FK_class_online_schedule_id && userClass.FK_class_online_schedule) {
    return formatOnlineScheduleLabel(userClass.FK_class_online_schedule);
  }
  return '';
}
